import { all, take, put, select, takeEvery } from 'redux-saga/effects'
import { channel } from 'redux-saga'
import { completeMultipartUploadAction, deleteFileS3 } from '../actions/tips'

import axios from 'axios'

import {
  FILE_REMOVE,
  FILE_SELECT,
  FILES_SET,
  FILE_UPLOAD_S3_FAIL,
  FILE_URL_REQUEST_FAIL,
  FILE_MULTIPART_UPLOAD_REQUEST_SUCCESS,
  FILE_COMPLETE_MULTIPART_UPLOAD_REQUEST_SUCCESS
} from '@redux/actions/types'

import { ERROR_MODAL_OPEN, SET_TIP_DATA } from '../actions/types'

const getFiles = state => state.tip.fileUploads

const uploadProgressChannel = channel()

/**
 * Adds a selected file to the upload queue and sets it as the current upload.
 * @param action
 */
function * onSelectFile(action) {
  const { payload: file } = action

  const files = yield select(getFiles)

  const fileUploads = [file, ...files]

  yield put({ type: FILES_SET, payload: { currentFileUpload: file, fileUploads, isUploadingFile: true } })
}

/**
 * Removes a file from the upload queue and deletes it from S3.
 * @param action
 */
function * onRemoveFile(action) {
  const { payload: fileId } = action

  const files = yield select(getFiles)
  
  const fileUploads = files.filter(image => image.fileId !== fileId)

  yield put(deleteFileS3({ fileId }))

  yield put({ type: FILES_SET, payload: { currentFileUpload: null, fileUploads, isUploadingFile: false } })
}

/**
 * Uploads file chunks to S3 using signed URLs and completes the multipart upload.
 * @param action
 */
function * onFileMultipartRequestSuccess(action) {
  const files = yield select(getFiles)
  const { fileKey, signedUrls, uploadId } = action.payload.data

  const { blob, type } = files.filter(file => file.fileId === fileKey)[0]

  delete axios.defaults.headers.put['Content-Type']

  const CHUNK_SIZE = 10000000

  const queuedUrls = yield signedUrls.map((url, i) => {
    const index = parseInt(i, 10)
    const start = index * CHUNK_SIZE
    const end = (index + 1) * CHUNK_SIZE
    const blobSlice = index < signedUrls.length ? blob.slice(start, end) : blob.slice(start)

    const onUploadProgress = progressEvent => {
      const { total, loaded } = progressEvent
      if (total !== null) {
        const uploadProgress = Math.round((loaded * 100) / total)
        uploadProgressChannel.put({ type: FILES_SET, payload: { uploadProgress } })
      }
    }

    return axios.put(url, blobSlice, { onUploadProgress })
  })

  const responses = yield all(queuedUrls)

  const uploadParts = responses.map((response, index) => ({
    ETag: response.headers.etag,
    PartNumber: index + 1
  }))
  
  const request = {
    data: {
      fileKey,
      uploadId,
      parts: uploadParts,
      requestType: 'complete',
      type
    }
  }
  yield put(completeMultipartUploadAction(request))
}

/**
 * Clears the current file upload state after multipart upload completes.
 */
function * onFileCompleteMultipartRequestSuccess() {
  yield put({ type: SET_TIP_DATA, payload: { currentFileUpload: null, isUploadingFile: false } })
}

/**
 * Removes the failed file from the queue and shows an error modal on upload failure.
 * @param action
 */
function * onFileUploadFail(action) {
  const files = yield select(getFiles)
  const fileKey = action.meta.previousAction.payload.request.data.fileKey
  const path = action.meta.previousAction.payload.request.data

  const removeFirestoreFail = files.filter(({ fileId }) => fileId !== fileKey)
  const removeS3Fail = files.filter(({ file }) => file !== path)
  const fileUploads = fileKey ? removeFirestoreFail : removeS3Fail

  yield put({ type: FILES_SET, payload: { fileUploads, isUploadingFile: false } })
  yield put({ type: ERROR_MODAL_OPEN, payload: { message: 'Your file could not be uploaded at this time. Please try again.' } })
}

/**
 * Root saga that watches for tip file upload actions and upload progress events.
 */
function * watchFiles() {
  yield takeEvery(FILE_REMOVE, onRemoveFile)
  yield takeEvery(FILE_SELECT, onSelectFile)
  yield takeEvery(FILE_UPLOAD_S3_FAIL, onFileUploadFail)
  yield takeEvery(FILE_URL_REQUEST_FAIL, onFileUploadFail)
  yield takeEvery(FILE_MULTIPART_UPLOAD_REQUEST_SUCCESS, onFileMultipartRequestSuccess)
  yield takeEvery(FILE_COMPLETE_MULTIPART_UPLOAD_REQUEST_SUCCESS, onFileCompleteMultipartRequestSuccess)
  while (true) {
    const action = yield take(uploadProgressChannel)
    yield put(action)
  }
}

export default watchFiles

